import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Portal from "@mui/material/Portal";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isApiError } from "@bw/shared/api";
import { classLearningQueryKeys, useAuth, useClassAttendanceRoster } from "@bw/shared/hooks";
import { markAttendance, type AttendanceStatus } from "@/libs/programsApi";
import { ClassFeedbackPanel } from "./program-learning-section";

const STATUSES: { value: AttendanceStatus; label: string }[] = [
  { value: "present", label: "Present" }, { value: "absent", label: "Absent" }, { value: "late", label: "Late" },
];
interface AttendanceProps {
  trackId: string;
  occurrenceStart: string;
  trackName: string | null;
  open: boolean;
  onClose: () => void;
}

/** One mounted roll belongs to one actor, track and immutable base recurrence. */
export function AttendanceDialog(props: AttendanceProps) {
  const { user, loading } = useAuth();
  if (loading || !user) return null;
  return <AttendanceScope key={user.uid + ":" + props.trackId + ":" + props.occurrenceStart}
    {...props} actorId={user.uid} />;
}

function AttendanceScope(props: AttendanceProps & { actorId: string }) {
  const [confirmed, setConfirmed] = useState(false);
  return <>
    {props.open ? <ScopedAttendance {...props} onSaved={() => setConfirmed(true)} /> : null}
    <Portal><Snackbar open={confirmed} autoHideDuration={5000} onClose={() => setConfirmed(false)}>
      <Alert severity="success" variant="filled" onClose={() => setConfirmed(false)}>Attendance saved</Alert>
    </Snackbar></Portal>
  </>;
}

function ScopedAttendance({ trackId, occurrenceStart, trackName, onClose, actorId, onSaved }: AttendanceProps & { actorId: string; onSaved: () => void }) {
  const titleId = useId();
  const client = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [draft, setDraft] = useState<Record<string, AttendanceStatus>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<{ programId: string; enrollmentId: string } | null>(null);
  const alive = useRef(true);
  const submitting = useRef(false);
  useLayoutEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const roster = useClassAttendanceRoster(trackId, occurrenceStart, { offset });
  const data = roster.data;
  const rosterKey = classLearningQueryKeys.attendance(actorId, trackId, occurrenceStart);
  const save = useMutation({
    mutationKey: [...rosterKey, "save"],
    retry: false,
    mutationFn: (records: { student_id: string; status: AttendanceStatus }[]) =>
      markAttendance(trackId, { track_id: trackId, occurrence_start: occurrenceStart, records }),
  });
  const dirty = Object.keys(draft).length > 0;
  const records = data?.can_mark_attendance
    ? data.items.filter((row) => row.can_mark_attendance && draft[row.entitlement_id] !== undefined)
      .map((row) => ({ student_id: row.student_id, status: draft[row.entitlement_id]! }))
    : [];
  const canSave = Boolean(data) && !roster.isError && !roster.isFetching && records.length > 0 && !save.isPending;
  const dates = useMemo(() => {
    const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" };
    try { return new Intl.DateTimeFormat("en-US", { ...options, timeZone: data?.timezone || "UTC" }); }
    catch { return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }); }
  }, [data?.timezone]);
  function time(iso: string): string {
    const value = new Date(iso);
    return Number.isFinite(value.getTime()) ? dates.format(value) : "Time unavailable";
  }

  async function submit(): Promise<void> {
    if (submitting.current || !canSave || !data) return;
    submitting.current = true;
    setNotice(null);
    const programId = data.program_id;
    try {
      await save.mutateAsync(records);
      // Refresh from owners, never install a prior actor's returned records.
      await Promise.all([
        client.invalidateQueries({ queryKey: rosterKey }),
        client.invalidateQueries({ queryKey: ["class-learning", actorId, "progress", programId] }),
      ]);
      if (alive.current) {
        setDraft({});
        onSaved();
        onClose();
      }
    } catch (error) {
      const changed = isApiError(error) && [401, 403, 404, 409, 422].includes(error.status);
      if (alive.current) {
        if (changed) setDraft({});
        setNotice(changed
          ? "This roll or your access changed. Review the refreshed roster before making changes."
          : "Couldn't save attendance. Your changes have been kept; try again.");
      }
      if (changed) await client.resetQueries({ queryKey: rosterKey });
    } finally {
      submitting.current = false;
    }
  }

  return <>
    <Drawer anchor="right" open variant="persistent" slotProps={{ paper: {
      role: "dialog", "aria-labelledby": titleId,
      sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100vw", boxSizing: "border-box" },
    } }}>
      <Stack sx={{ height: "100%" }}>
        <Stack direction="row" sx={{ p: 3, borderBottom: 1, borderColor: "divider", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography component="h2" variant="h6" id={titleId}>Take attendance</Typography>
            {trackName ? <Typography variant="body2" color="text.secondary">{trackName}</Typography> : null}
          </Box>
          <IconButton size="small" aria-label="Close attendance" disabled={save.isPending} onClick={onClose}><CloseRounded /></IconButton>
        </Stack>
        <Stack spacing={2} sx={{ p: 3, flex: 1, overflowY: "auto" }}>
          {notice ? <Alert severity="warning">{notice}</Alert> : null}
          {roster.isError ? <Alert severity="error" action={<Button size="small" onClick={() => void roster.refetch()}>Retry</Button>}>
            We couldn't load this session roster. Your access may have changed.
          </Alert> : !data ? <Typography role="status">Loading session roster…</Typography> : <>
            <Typography variant="body2" color="text.secondary">
              {time(data.effective_start)} – {time(data.effective_end)}
              {data.state === "scheduled" ? "" : " · " + data.state}
            </Typography>
            {data.items.length === 0 ? <Typography>No learners on this session page.</Typography> : <Stack component="ul" spacing={2} sx={{ listStyle: "none", m: 0, p: 0 }}>
              {data.items.map((row) => {
                const label = row.display_name?.trim() || "—";
                const editable = data.can_mark_attendance && row.can_mark_attendance;
                return <Stack component="li" key={row.entitlement_id} spacing={1}>
                  <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{label}</Typography>
                    {data.can_write_feedback && row.can_write_feedback ? <Button size="small" disabled={save.isPending || dirty}
                      onClick={() => setSelectedFeedback({ programId: data.program_id, enrollmentId: row.enrollment_id })}>Feedback</Button> : null}
                  </Stack>
                  {editable ? <ToggleButtonGroup aria-label={"Attendance status for " + label} exclusive fullWidth size="small"
                    disabled={save.isPending || roster.isFetching}
                    value={draft[row.entitlement_id] ?? (row.attendance === "unmarked" ? null : row.attendance)}
                    onChange={(_event, value: AttendanceStatus | null) => {
                      if (value === null) return;
                      setDraft((previous) => {
                        const next = { ...previous };
                        if (value === row.attendance) delete next[row.entitlement_id];
                        else next[row.entitlement_id] = value;
                        return next;
                      });
                    }}>
                    {STATUSES.map((status) => <ToggleButton key={status.value} value={status.value} aria-label={status.label}>{status.label}</ToggleButton>)}
                  </ToggleButtonGroup> : <Typography variant="body2" color="text.secondary">
                    {row.attendance === "unmarked" ? "Unmarked" : row.attendance} · Read-only
                  </Typography>}
                </Stack>;
              })}
            </Stack>}
            {dirty ? <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">Save or discard your changes before changing page.</Typography>
              <Button size="small" disabled={save.isPending} onClick={() => setDraft({})}>Discard changes</Button>
            </Stack> : null}
            <Stack direction="row" spacing={1}>
              <Button size="small" disabled={dirty || save.isPending || data.offset <= 0}
                onClick={() => setOffset(Math.max(0, data.offset - data.limit))}>Previous</Button>
              <Button size="small" disabled={dirty || save.isPending || data.offset + data.limit >= data.total}
                onClick={() => setOffset(data.offset + data.limit)}>Next</Button>
            </Stack>
          </>}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ p: 2, borderTop: 1, borderColor: "divider", justifyContent: "flex-end" }}>
          <Button size="small" disabled={save.isPending} onClick={onClose}>Close</Button>
          <Button size="small" variant="contained" disabled={!canSave} onClick={() => void submit()}>Save attendance</Button>
        </Stack>
      </Stack>
    </Drawer>
    {selectedFeedback ? <ClassFeedbackPanel programId={selectedFeedback.programId} enrollmentId={selectedFeedback.enrollmentId}
      onClose={() => setSelectedFeedback(null)} /> : null}
  </>;
}
